import { useCallback, useEffect, useMemo, useState } from "react";
import type { SavedView } from "./DataTable.types";
import { createLocalViewStore, createTableViewSelectorStore, type ViewStore } from "./viewStore";
import type { SaveViewApiConfig } from "./saveViewApi";

export type UseSavedViewsOptions = {
  /** Optional read adapter. Defaults to the localStorage-backed store for `screenName`. */
  store?: ViewStore;
  /**
   * Saved-view API config. When supplied, the primary view is hydrated with its
   * full columns/filters via the API — the local mirror only holds the list
   * summary — so a default view's columns/filters can be applied on load.
   * Memoize this object so the hook doesn't re-fetch every render.
   */
  saveApi?: SaveViewApiConfig;
  /** Set false to keep the hook idle while a screen key is still resolving. */
  enabled?: boolean;
};

export type UseSavedViewsResult = {
  views: SavedView[];
  primaryView: SavedView | null;
  loading: boolean;
  error: unknown;
  refresh: () => Promise<SavedView[]>;
};

/**
 * Read saved views for a screen without exposing the saved-view HTTP helpers.
 *
 * Components that own the backend sync write into the same screen-scoped local
 * store; this hook subscribes to that store so consumers can react to the latest
 * mirrored list.
 */
export function useSavedViews(screenName: string | undefined, options: UseSavedViewsOptions = {}): UseSavedViewsResult {
  const { store, saveApi, enabled = true } = options;
  const resolvedStore = useMemo(() => {
    if (!screenName) return undefined;
    return store ?? createLocalViewStore(screenName);
  }, [screenName, store]);
  // Remote-capable store used only to fetch a single view's full detail. Kept
  // separate so listing still reads the local mirror (no extra list call).
  const hydrationStore = useMemo(() => {
    if (store || !saveApi || !screenName) return undefined;
    return createTableViewSelectorStore(screenName, { saveApi });
  }, [screenName, store, saveApi]);
  const [views, setViews] = useState<SavedView[]>([]);
  const [hydratedPrimary, setHydratedPrimary] = useState<SavedView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const refresh = useCallback(async () => {
    if (!enabled || !resolvedStore) {
      setViews([]);
      setLoading(false);
      setError(null);
      return [];
    }

    setLoading(true);
    setError(null);
    try {
      const next = await resolvedStore.list();
      setViews(next);
      return next;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [enabled, resolvedStore]);

  useEffect(() => {
    if (!enabled || !resolvedStore) {
      setViews([]);
      setLoading(false);
      setError(null);
      return;
    }

    let active = true;
    const load = () => {
      setLoading(true);
      setError(null);
      void resolvedStore
        .list()
        .then((next) => {
          if (active) setViews(next);
        })
        .catch((err) => {
          if (active) setError(err);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    };

    load();
    const unsubscribe = resolvedStore.subscribe(load);
    return () => {
      active = false;
      unsubscribe();
    };
  }, [enabled, resolvedStore]);

  const summaryPrimary = useMemo(() => views.find((view) => view.is_default) ?? null, [views]);
  const summaryPrimaryId = summaryPrimary?.view_id ?? null;

  // Fetch the primary view's full detail (columns/filters) via the API.
  useEffect(() => {
    if (!enabled || !hydrationStore || !summaryPrimaryId) {
      setHydratedPrimary(null);
      return;
    }
    let active = true;
    void hydrationStore
      .get(summaryPrimaryId)
      .then((detail) => {
        if (active && detail) setHydratedPrimary(detail);
      })
      .catch(() => {
        /* keep the summary primary on failure */
      });
    return () => {
      active = false;
    };
  }, [enabled, hydrationStore, summaryPrimaryId]);

  // Prefer the hydrated primary once it matches the current primary id.
  const primaryView =
    hydratedPrimary && hydratedPrimary.view_id === summaryPrimaryId ? hydratedPrimary : summaryPrimary;

  // Reflect the hydrated primary back into the list so consumers that read the
  // default view off `views` also get its columns/filters.
  const hydratedViews = useMemo(() => {
    if (!primaryView || primaryView === summaryPrimary) return views;
    return views.map((view) => (view.view_id === primaryView.view_id ? primaryView : view));
  }, [views, primaryView, summaryPrimary]);

  return { views: hydratedViews, primaryView, loading, error, refresh };
}
